import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import customizationsAPI from '../../api/customizationsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Page = styled.main`max-width: 680px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0 0 1.25rem;`;
const Card = styled.section`
  background: var(--bg-primary, #fff); border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.75rem; margin-bottom: 1rem; padding: 1.2rem;
`;
const ToggleRow = styled.label`align-items: flex-start; cursor: pointer; display: flex; gap: 1rem; justify-content: space-between;`;
const SectionTitle = styled.h2`color: var(--text-primary); font-size: 1rem; margin: 0 0 0.45rem;`;
const Helper = styled.p`color: var(--text-secondary); font-size: 0.82rem; line-height: 1.5; margin: 0.35rem 0 0;`;
const Radio = styled.label`align-items: center; cursor: pointer; display: flex; gap: 0.55rem; padding: 0.5rem 0;`;
const Label = styled.label`display: block; font-size: 0.875rem; font-weight: 700; margin: 1rem 0 0.4rem;`;
const Input = styled.input`
  border: 1.5px solid ${(p) => p.$error ? '#dc2626' : 'var(--border-color, #d1d5db)'};
  border-radius: 0.55rem; box-sizing: border-box; font: inherit; padding: 0.75rem; width: 100%;
`;
const Error = styled.p`color: #b91c1c; font-size: 0.8rem; margin: 0.35rem 0 0;`;
const Save = styled.button`
  background: #1b4332; border: 0; border-radius: 0.55rem; color: #fff; cursor: pointer;
  font-weight: 800; padding: 0.85rem 1.3rem; &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const State = styled.div`padding: 4rem 1rem; text-align: center;`;

const ArtistCustomizationSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accepts, setAccepts] = useState(false);
  const [priceMode, setPriceMode] = useState('discussed');
  const [minimum, setMinimum] = useState('');
  const [maxWidth, setMaxWidth] = useState('');
  const [maxHeight, setMaxHeight] = useState('');
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const settings = await customizationsAPI.getArtistSettings();
      setAccepts(Boolean(settings.accepts_customizations));
      setPriceMode(settings.pricing_to_be_discussed ? 'discussed' : 'minimum');
      setMinimum(settings.customization_min_price_cents == null ? '' : String(Number(settings.customization_min_price_cents) / 100));
      setMaxWidth(settings.customization_max_width_cm == null ? '' : String(settings.customization_max_width_cm));
      setMaxHeight(settings.customization_max_height_cm == null ? '' : String(settings.customization_max_height_cm));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const nextErrors = {};
    const parsedMinimum = Number(minimum);
    const width = Number(maxWidth);
    const height = Number(maxHeight);
    if (accepts && priceMode === 'minimum' && (!Number.isFinite(parsedMinimum) || parsedMinimum <= 0)) {
      nextErrors.minimum = 'Enter a minimum customization price greater than 0.';
    }
    if (Boolean(maxWidth) !== Boolean(maxHeight)) {
      nextErrors.dimensions = 'Enter both maximum width and height, or leave both blank.';
    } else if (maxWidth && (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0)) {
      nextErrors.dimensions = 'Enter a number greater than 0.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      await customizationsAPI.updateArtistSettings({
        accepts_customizations: accepts,
        customization_min_price_cents: accepts && priceMode === 'minimum' ? Math.round(parsedMinimum * 100) : null,
        pricing_to_be_discussed: accepts && priceMode === 'discussed',
        customization_max_width_cm: maxWidth ? width : null,
        customization_max_height_cm: maxHeight ? height : null,
      });
      toast.success('Customization settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading customization settings…" />;
  if (loadError) return <Page><State><h2>Something went wrong. Try again.</h2><Save type="button" onClick={load}>Try again</Save></State></Page>;

  return (
    <Page>
      <Title>Customization settings</Title>
      <Card>
        <ToggleRow>
          <div>
            <SectionTitle>Accept customization requests</SectionTitle>
            <Helper>Turn this off to stop appearing in new recommendations. Requests already sent to you are not affected.</Helper>
          </div>
          <input type="checkbox" checked={accepts} onChange={(e) => setAccepts(e.target.checked)} />
        </ToggleRow>
      </Card>
      {accepts ? (
        <Card>
          <SectionTitle>Starting price</SectionTitle>
          <Radio><input type="radio" name="price-mode" checked={priceMode === 'minimum'} onChange={() => setPriceMode('minimum')} />Set a minimum customization price</Radio>
          <Radio><input type="radio" name="price-mode" checked={priceMode === 'discussed'} onChange={() => setPriceMode('discussed')} />To be discussed</Radio>
          {priceMode === 'minimum' ? (
            <>
              <Label htmlFor="minimum-price">Minimum customization price</Label>
              <Input id="minimum-price" type="number" min="0.01" step="0.01" value={minimum} $error={errors.minimum} onChange={(e) => setMinimum(e.target.value)} />
              {errors.minimum ? <Error>{errors.minimum}</Error> : null}
              <Helper>This is a guide for buyers, not a fixed price. You can send a lower or higher quote for a specific request.</Helper>
            </>
          ) : <Helper>Buyers will see ‘To be discussed’.</Helper>}
        </Card>
      ) : null}
      <Card>
        <SectionTitle>Maximum canvas size (optional)</SectionTitle>
        <Label htmlFor="max-width">Maximum width (cm)</Label>
        <Input id="max-width" type="number" min="0.01" step="0.01" value={maxWidth} $error={errors.dimensions} onChange={(e) => setMaxWidth(e.target.value)} />
        <Label htmlFor="max-height">Maximum height (cm)</Label>
        <Input id="max-height" type="number" min="0.01" step="0.01" value={maxHeight} $error={errors.dimensions} onChange={(e) => setMaxHeight(e.target.value)} />
        {errors.dimensions ? <Error>{errors.dimensions}</Error> : null}
        <Helper>Buyers requesting a larger canvas won’t see your gallery in matched results. Leave both blank if you don’t have a limit.</Helper>
      </Card>
      <Save type="button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Save>
    </Page>
  );
};

export default ArtistCustomizationSettingsPage;
